import Opener, { OpenerSlotProps } from '@Ravnur-Inc/core/components/opener/opener';
import SelectableList from '@Ravnur-Inc/core/components/selectable-list/selectable-list';
import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import flow from '@Ravnur-Inc/nanoutils/flow';
import { ErrorObject } from '@vuelidate/core';
import { Vue, Options, prop } from 'vue-class-component';

import { times } from '@/helpers/date';
import { time } from '@/transformers/date';

import { StandardOption } from '@/types/Component';
import MaskedInput from '@Ravnur-Inc/core/components/masked-input/masked-input';

import './timepicker.scss';

const CN = 'timepicker';
const date2option: (d: Date) => StandardOption = flow(time, time2option);
const TIME_STEP = 15;
const TIME_REGEX = /((1[0-2]|0?[1-9]):([0-5][0-9]) ?([AaPp][Mm]))/;

class Props {
  value = prop<Nullable<string>>({ default: null });
  label = prop({ default: '' });
  placeholder = prop({ default: '' });
  errors = prop<ErrorObject[]>({ default: [] });
  requiredAsterisk = prop({ default: false });
}

type Emits = {
  onSelect: (time: string) => void;
};

const FORMATS = {
  ['H']: {
    validate: (char: string) => /^[0-1]$/.test(char),
  },
  ['h']: {
    validate: (char: string) => /^[0-9]$/.test(char),
  },
  ['M']: {
    validate: (char: string) => /^[0-5]$/.test(char),
  },
  ['m']: {
    validate: (char: string) => /^[0-9]$/.test(char),
  },
  ['P']: {
    validate: (char: string) => /^[aApP]$/.test(char),
    transform: (char: string) => char.toUpperCase(),
  },
  ['T']: {
    validate: (char: string) => /^[mM]$/.test(char),
    transform: (char: string) => char.toUpperCase(),
  },
};

@Options({
  name: 'time-picker',
  emits: ['select'],
})
export default class TimePicker extends Vue.with(Props) {
  declare $props: $Props<Props, Emits>;
  declare $refs: {
    opener: Opener;
  };

  private get items(): StandardOption[] {
    return times(TIME_STEP).map(date2option);
  }

  private get current() {
    return this.items.find(({ id }) => id === this.value) || null;
  }

  private get localValue() {
    return this.value || '';
  }

  render() {
    const slots = {
      summary: this.renderTextField,
      details: this.renderOptions,
    };
    return <Opener ref="opener" class={CN} v-slots={slots} />;
  }

  private renderTextField({ toggle }: OpenerSlotProps) {
    return (
      <MaskedInput
        class={`${CN}__field`}
        customFormats={FORMATS}
        errors={this.errors}
        icon="clock"
        label={this.label}
        mask={'Hh:Mm PT'}
        onfocus={toggle}
        placeholder={this.placeholder}
        requiredAsterisk={this.requiredAsterisk}
        value={this.localValue}
        onInput={this.handleInput}
      />
    );
  }

  private renderOptions() {
    return (
      <SelectableList
        current={this.current}
        items={this.items}
        propName="id"
        onSelect={this.select}
      />
    );
  }

  private select(entity: StandardOption) {
    this.$emit('select', entity.id);
    this.$refs.opener.hide();
  }

  private handleInput(value: string) {
    const regex = TIME_REGEX;
    let time = value;

    if (!regex.test(value)) {
      time = '';
    }

    this.$emit('select', time);
  }
}

function time2option(t: string): StandardOption {
  return { id: t, label: t };
}
